import XUICapsule from '@xero/xui/react/capsule';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import IllustrationInvoice from '../../../assets/svgs/InvoiceIllustrationSVG';
import Link from '../../../components/Link';
import { getEnv, isDocker, isLocal, isUat } from '../../../helpers/env';
import { IShopifySettingsResponse } from '../../../models';
import { settingsSelectors } from '../../../redux/slices/SettingsSlice';

const EDUCATION_FLOW = () => {
    const WIZARD = [
        {
            SVG: <IllustrationInvoice />,
            HEADING: 'Track your Shopify sales in Xero',
            CONTENT: 'Get a daily summarised invoice of your Shopify sales for each of your payment gateways',
        },
    ];

    return WIZARD;
};

const ConnectedBlurb: FC<object> = () => {
    const settings = useSelector(settingsSelectors.settings) as IShopifySettingsResponse;

    if (settings) {
        lang.CONNECTED_PAGE.SUCCESS_ACTION_URL = lang.CONNECTED_PAGE.SUCCESS_ACTION_URL.replace(
            '{host}',
            settings?.shop ?? ''
        );
    }

    const contactUrl = isDocker()
        ? `http://localhost:8035/Contacts/View/${settings?.contactId}`
        : `https://go.${getEnv()}/Contacts/View/${settings?.contactId}`;

    return (
        <div>
            <p>Below is an activity report showing what's been sent from Shopify to Xero.</p>
            <p>
                Shopify invoices can be found in <b>Contacts</b> under your Shopify store name. <br />
                <Link displayLinkIcon={true} href={contactUrl}>
                    View Shopify invoices
                </Link>
            </p>
        </div>
    );
};

const lang = {
    DISPLAY_NAME: 'Shopify integration by Xero',
    INTEGRATION_NAME: 'Shopify',
    SIGN_UP_NAME: 'Shopify',
    HAS_PARTNER_STORE: true,
    TAGS: ['Retail', 'Ecommerce'],
    ACCESS_PAGE: {
        FREE_TRIAL_DETAILS: (
            <div>
                Free for 30 days, no payment details required
                <br />
                Paid subscription thereafter
            </div>
        ),
        FREE_TRIAL_BLURB: (
            <p>
                Start your <b>30-day free trial</b> for access to all features included in the Shopify integration by
                Xero.
            </p>
        ),
        FREE_TRIAL_SIGNUP_BLURB: 'You’ll need a Shopify account to start the free trial.',
        CONNECT_BLURB: (
            <p>Start your connection for access to all features included in the Shopify integration by Xero.</p>
        ),
        CONNECT_SIGNUP_BLURB: 'You’ll need a Shopify account to start the connection.',
        SIGNUP_LINK_TEXT: 'Sign up for a Shopify account',
        CONNECT_DOES_NOT_SUPPORT_TAX_BLURB: (
            <p>
                Shopify integration by Xero <b>does not account for sales tax</b> and{' '}
                <b>can’t be used by tax registered businesses</b>.
            </p>
        ),
        SUPPORTED_FEATURES: {
            FEATURES: [
                'Automatically syncs daily Shopify sales transactions to Xero',
                'Creates a daily summary invoice for preferred payment gateways',
                'Shows sales, discounts, shipping costs, refunds, gift card payments, and fees across all invoices',
                'Tracks pending sales from Shopify payments and PayPal by adding a clearing account',
                'Transfers processed sales from Shopify payments and PayPal into a nominated bank account',
                'Imports the previous 90 days of Shopify sales transactions (only available with Xero Standard or Premium plans)',
                'Handling of multiple sales tax rates for NZ, AU and UK regions',
            ],
            HEADING: 'Features',
        },
        AU_SUPPORTED_FEATURES: {
            FEATURES: [
                'Automatically syncs daily Shopify sales transactions to Xero',
                'Creates a daily summary invoice for preferred payment gateways',
                'Shows sales, discounts, shipping costs, refunds, gift card payments, and fees across all invoices',
                'Tracks pending sales from Shopify payments and PayPal by adding a clearing account',
                'Transfers processed sales from Shopify payments and PayPal into a nominated bank account',
                'Imports the previous 90 days of Shopify sales transactions (only available with Xero Grow, Comprehensive or Ultimate plans)',
                'Handling of multiple sales tax rates for NZ, AU and UK regions',
            ],
            HEADING: 'Features',
        },
        UNSUPPORTED_FEATURES: {
            FEATURES: [
                'Connection of multiple Shopify stores to a Xero account',
                'Shopify stores and Xero accounts with different currencies',
                'Automatic tax filing in multiple tax jurisdictions',
                'Invoices for each Shopify sales transaction',
                'Invoices showing customer (payment) details',
                'Inventory and Cost of Goods Sold management',
            ],
            HEADING: 'Not supported in the Shopify integration by Xero',
        },
        TAX_DISCLAIMER:
            'Please note that the records created in Xero by this integration may not be considered to be sufficient for audit purposes. This is because records of individual sales and purchases are only stored within Shopify and this information is not copied across to Xero. Owing to this, you may need to maintain your Shopify subscription or export additional information from Shopify in order to meet local record keeping requirements. Check with your accountant or bookkeeper if you are unsure about what you need to do.',
        MODAL_TITLE: 'Connect Shopify to Xero',
        MODAL_BODY: 'Your online sales and transactions will sync to Xero for easy reconciliation',
        MODAL_CANCEL_BUTTON: 'Cancel',
        MODAL_CONNECT_BUTTON: 'Connect',
        MODAL_INPUT_HOSTNAME_LABEL: 'Shopify store address',
        MODAL_INPUT_HOSTNAME_PLACEHOLDER: 'myshop.myshopify.com',
        MODAL_INPUT_HOSTNAME_VALIDATION_ERROR:
            'Invalid shop address, it should look something like myshop.myshopify.com',
        DECOMMISSION_BANNER: {
            HEADING: 'Action needed: Move to the new free Shopify integration by Xero before 8 July 2026',
            BODY: (hasBought: boolean) => (
                <>
                    We are retiring your current Shopify integration by Xero on <b>8 July 2026</b>. You will not be able to
                    access your legacy installation beyond this date.{' '}
                    {hasBought ? 'Your subscription is in the process of being refunded.' : ''}
                </>
            ),
            BODY_ACTION: (
                <>
                    To keep your Shopify sales syncing connect to the new <b>free</b> Shopify integration by Xero (built
                    by Amaka) and then disconnect the old integration.
                </>
            ),
            ACTION_BUTTON_TEXT: 'Shopify integration by Xero',
            ACTION_BUTTON_URL: (isProd: boolean) =>
                isProd ? 'https://apps.xero.com/app/shopify' : 'https://apps.livestage6.test.xero.com/app/shopify',
        },
    },
    CONNECTED_PAGE: {
        BLURB: <ConnectedBlurb />,
        CLOSE_TOAST: 'Close',
        SUCCESS_SETUP_MESSAGE: 'Your Shopify store has been connected',
        SUCCESS_ACTION_LABEL: 'Return to Shopify',
        SUCCESS_ACTION_URL: 'https://{host}/admin/apps/' + (isLocal() ? 'xero-dev' : isUat() ? 'xero-uat' : 'xero-7'),
        FREE_TRIAL: {
            SUCCESS_SETUP_MESSAGE:
                'Your Shopify store is now connected and your free trial of the Shopify integration by Xero has started',
            SUBSCRIBE_NOW_BUTTON: 'Subscribe now',
            REMAINING_DAYS_TAG_TITLE: ' days left in your trial',
            EXPIRING_TODAY_TAG_TITLE: 'Trial expires today',
            EXPIRED_TAG_TITLE: 'Trial expired',
        },
    },
    SETUP_PAGE: {
        TAB_NAMES: ['Payment gateways', 'Shopify revenue', 'Shopify expenses', 'Additional settings'],
        TAB_NAMES_V2: ['Account & tax setup', 'Payment gateways', 'Import data'],
        REFRESH_PLAN_ERROR_TEXT: 'Refresh this page to access your updated plan',
        REFRESH: 'Refresh',
        STORE: {
            TITLE: 'Payment gateways',
            DESCRIPTION: (
                <div>
                    <p>
                        The payment gateways used in your Shopify store have been added automatically. To continue the
                        setup:
                    </p>
                    <p>
                        1. Check the pre-populated payment gateway details
                        <br />
                        2. Add a{' '}
                        <Link
                            displayLinkIcon={true}
                            href={
                                'https://central.xero.com/services/auth/sso/Xero/?startURL=/s/article/Connect-to-Shopify?userregion=true'
                            }
                        >
                            clearing account
                        </Link>{' '}
                        for each gateway and select a bank account for Shopify Payments and PayPal for easier
                        reconciliation
                        <br />
                        3. Manually add any additional payment gateways
                    </p>
                </div>
            ),
            CREATE_INVOICE: 'Yes',
            DON_T_CREATE_INVOICE: 'No',
            ROW_BUTTON_REMOVE_ARIA: 'Remove row',
            ROW_HEADER_PAYMENT_GATEWAY: 'Payment gateway',
            ROW_HEADER_CREATE_INVOICE: 'Create invoice',
            ROW_HEADER_CREATE_INVOICE_POPOVER:
                'If you select ‘yes’, we will generate a daily summarised invoice for that payment gateway',
            ROW_HEADER_CLEARING_ACCOUNT: 'Clearing account',
            ROW_HEADER_CLEARING_ACCOUNT_POPOVER:
                'Clearing accounts track all sales made through payment gateways until they can be reconciled with the money in your bank account. Leaving the clearing account option blank will mean that daily invoices will need to be manually reconciled.',
            TAX_DISCLAIMER:
                'Xero will obtain information regarding your sales and taxes from Shopify. In order to make sure that the information in your Xero subscription is correct, you will need to make sure that the tax information contained in Shopify is accurate.',
            ROW_HEADER_BANK_ACCOUNT: 'Bank account',
            ROW_HEADER_OPTIONAL: '(optional)',
            ROW_HEADER_BANK_ACCOUNT_POPOVER: 'Choose the bank account that Shopify Payments or PayPal pays into',
            ROW_SELECT_PAYMENT_TYPE: 'Select payment gateway',
            ADD_PAYMENT: 'Add payment gateway',
            CANNOT_RECONCILE: (
                <div>
                    Transactions for this payment gateway will need to be manually reconciled.{' '}
                    <Link
                        displayLinkIcon={true}
                        href={'https://central.xero.com/s/article/Reconcile-your-bank-account#Web'}
                    >
                        Learn more
                    </Link>
                </div>
            ),
            PAYMENT_GATEWAY_HELP: {
                POPULAR: 'Popular',
                PREAMBLE:
                    "Missing a payment gateway? If you can't see your payment gateway listed, please check back after a sale has been made through that gateway.",
            },
            CLEARING_ACCOUNT_CREATED_TOAST: 'Clearing account created',
            TABLE_PG_CELL_VALIDATION_MESSAGE: 'Payment gateway required',
            TABLE_HINT_MESSAGE:
                "Invoices will be created by default for all payment gateways not configured in this table. Set 'Create Invoice' to 'No' if you do not want an invoice created for the payment gateway.",
            BANK_ACCOUNT_DELETED: 'Bank account deleted',
            BANK_ACCOUNT_DELETED_MESSAGE:
                'Check the payment gateway details to make sure transactions from Shopify can still be imported.',
            CLEARING_ACCOUNT_REQUIRED: 'Clearing account required',
            DISMISS_BUTTON: 'Dismiss',
        },
        CLEARING_ACCOUNT: {
            SELECT: 'Select clearing account',
            TITLE: 'Clearing account',
            DESCRIPTION:
                'This integration will create daily sales invoices. Select a clearing account, which will be used to hold this information while waiting for the Shopify payout to occur',
            CLEARING_ACCOUNT: 'Clearing account',
            CLEARING_ACCOUNT_HINT:
                'This is used to temporarily hold your payout information until the money is received',
            ADVISOR_PREAMBLE: 'Need more help? Talk to an ',
            ADVISOR_LINK: 'Advisor',
            CREATE_CLEARING_ACCOUNT_BUTTON: 'Create clearing account',
            MODAL_HEADER: 'Create clearing account',
            MODAL_MESSAGE: 'This is an account in Xero where your payments are sorted into',
            CREATE_ACCOUNT_ERROR: "We're unable to save this clearing account. Please try again.",
            ACCOUNT_NAME_NOT_UNIQUE_ERROR_MESSAGE: 'Account name must be unique',
            ACCOUNT_NAME_LABEL: 'Account name',
            ACCOUNT_NAME_HINT: 'Please enter an account name',
            DEFAULT_BANK_ACCOUNT_NAME: 'Shopify clearing account',
            CANCEL_BUTTON: 'Cancel',
            CREATE_BUTTON: 'Create',
        },
        DELETE_PAYMENT_GATEWAY: {
            MODAL_HEADER: 'Delete payment gateway',
            MODAL_MESSAGE: (
                <div>
                    If a new sale is made using this payment gateway, it will appear in a daily invoice. Try turning off
                    invoices instead, by selecting 'No' in the <b>Create invoice</b> dropdown.
                </div>
            ),
            CANCEL_BUTTON: 'Cancel',
            DELETE_BUTTON: 'Delete',
        },
        ADVISOR_PREAMBLE: 'Need more help? Talk to an ',
        ADVISOR_LINK:
            'https://www.xero.com/advisors/find-advisors/?type=advisors&tag[]=xero:advisor-directory/industries-served/e-commerce&orderBy=ADVISOR_RELEVANCE&sort=ASC&pageNumber=1',
        ADVISOR_TEXT: 'Advisor',
        BLURB: 'Something about the connection needs to be said here.',
        TITLE: 'Settings for Shopify',
        REVENUE_TAB: {
            TITLE: 'Check your Shopify revenue accounts',
            BLURB: (
                <div>
                    <p>
                        See whether the pre-populated chart of accounts are the accounts you'd like your Shopify revenue
                        allocated to.
                    </p>
                    <p>
                        If you choose an account that has a <b>New</b> tag, we'll create a default account for you.
                    </p>
                </div>
            ),
            SELECT_ACCOUNT: 'Select an account',
            SALES: 'Sales',
            DISCOUNTS: 'Discounts',
            SHIPPING: 'Shipping revenue',
            SHIPPING_HINT: 'The revenue your customers have paid you for shipping',
            REFUNDS: 'Refunds',
            COA_HELP_PREAMBLE: "Can't see your account? ",
            COA_HELP_LINK:
                'https://central.xero.com/services/auth/sso/Xero/?startURL=/s/article/Connect-to-Shopify?userregion=true',
            COA_HELP_TEXT: 'Learn about chart of accounts',
        },
        EXPENSES_TAB: {
            TITLE: 'Select your Shopify expense accounts',
            BLURB: (
                <div>
                    <p>Decide which accounts you'd like your Shopify expenses and liabilities allocated to.</p>
                    <p>
                        If you choose an account that has a <b>New</b> tag, we'll create a default account for you.
                    </p>
                </div>
            ),
            SELECT_ACCOUNT: 'Select an account',
            SHOPIFY_FEES: 'Shopify transaction fees',
            SHOPIFY_FEES_HINT: 'The fee charged per transaction not your monthly Shopify plan fee',
            TAX_COLLECTED: 'Tax collected',
            GIFTCARDS: 'Gift cards',
            GIFTCARDS_HINT: 'Select an account even if you don’t currently sell gift cards',
            COA_HELP_PREAMBLE: "Can't see your account? ",
            COA_HELP_LINK:
                'https://central.xero.com/services/auth/sso/Xero/?startURL=/s/article/Connect-to-Shopify?userregion=true',
            COA_HELP_TEXT: 'Learn about chart of accounts',
        },
        SYNC_TAB: {
            TAX_TITLE: 'Set up your Tax',
            TAX_COLLECT: 'Do you collect tax through your Shopify store?',
            TAX_NO: 'No',
            TAX_YES: 'Yes',
            TAX_AUTHORITY: 'Xero tax authority',
            TAX_AUTHORITY_HINT: 'Xero tax authority hint',
            TAX_AUTHORITY_SELECT: 'Select tax authority',
            TAX_RATE: 'Tax rate',
            TAX_RATE_SELECT: 'Select tax rate',
            SHOPIFY_TAX_REGION: 'Shopify tax region',
            SHOPIFY_TAX_REGION_SELECT: 'Select Shopify tax region',
            SHOPIFY_TAX_REGION_HINT: 'Select the Shopify tax region that is related to the Xero tax authority above',
            SYNC_NO: 'No',
            SYNC_YES: 'Yes',
            SYNC_TITLE: 'Import data',
            SYNC_PROGRESSIVE_DISCLOSURE_LABEL: 'Create invoices for dates prior to today?',
            SYNC_BLURB: (
                <div>
                    <p>This step is optional. It allows you to import data for the previous 90 days.</p>
                    <p>
                        If you select yes, make sure you haven't previously reconciled these dates or duplicate invoices
                        will be created.
                    </p>
                </div>
            ),
            SYNC_OPTIONAL: '(optional)',
            AU_SYNC_DISABLED_BLURB:
                'This option is only available to organisations on Grow, Comprehensive or Ultimate plans. ',
            SYNC_DISABLED_BLURB: 'This option is only available to organisations on Standard or Premium plan. ',
            SYNC_DISABLED_LINK: 'https://go.xero.com/Settings/ManageSubscription/',
            SYNC_DISABLED_LINK_TEXT: 'Upgrade your plan to import data for the previous 90 days',
            FOR: 'For ',
            FROM: 'From ',
            TO: ' to ',
            SYNC_RANGE_DATE_PICKER: 'Start date',
            SYNC_RANGE_DATE_PICKER_PLACEHOLDER: 'Select date',
            CHOOSE_SYNC_DATE_HELPER_TEXT: "Choose the date you'd like the import to start on",
            DISCLAIMER_TITLE: 'When you import previous data',
            DISCLAIMER_DETAILS_1:
                'An invoice per day per payment gateway will be created from the date you select below',
            DISCLAIMER_DETAILS_2:
                'If you chose a clearing account in step 1, each invoice will be paid to that account',
            DISCLAIMER_DETAILS_3:
                "If you've chosen a bank account for PayPal or Shopify Payments, a payout transfer will be created",
            LEARN_MORE: 'Learn more',
            LEARN_MORE_LINK: 'https://central.xero.com/s/article/Find-and-remove-duplicate-bank-statement-lines',
            TAX_DISCLAIMER:
                'Xero will obtain information regarding your sales and taxes from Shopify. In order to make sure that the information in your Xero subscription is correct, you will need to make sure that the tax information contained in Shopify is accurate.',
            TAX_RATE_VALIDATION_ERROR: 'Tax rate is required',
            SHOPIFY_TAX_REGION_VALIDATION_ERROR: 'Shopify tax region is required',
            TAX_AUTHORITIES: {
                AU: 'Australian Taxation Office',
                NZ: 'New Zealand Inland Revenue',
                UK: 'HM Revenue and Customs',
            },
            NO_SETTINGS_REQUIRED_TITLE: 'No additional settings required',
            NO_SETTINGS_REQUIRED_TEXT: 'Based on your current setup, no additional settings are required',
        },
        TAX_MAPPING_TABLE: {
            REGION_HEADING: 'Region',
            TRANSACTION_TYPE_HEADING: 'Transaction type',
            ACCOUNT_HEADING: 'Account',
            TAX_RATE_HEADING: 'Tax rate',
            REGION_POPOVER_CONTENT:
                'The countries/regions where you make sales in Shopify. All regions outside your primary tax region are grouped as ‘Global’',
            TRANSACTION_TYPE_POPOVER_CONTENT: 'The type of transactions seen in your Shopify sales',
            ACCOUNT_POPOVER_CONTENT:
                'The accounts available to you are based on the accounts set up in your Xero organisation and our optional Shopify accounts',
            TAX_RATE_POPOVER_CONTENT:
                'The tax rates available to you are based on the rates set up in your Xero organisation',
            ACCOUNT_CELL_PLACEHOLDER: 'Select account',
            ACCOUNT_CELL_VALIDATION_MESSAGE: 'Select an account',
            TAX_RATE_CELL_PLACEHOLDER: 'Select tax rate',
            TAX_RATE_CELL_VALIDATION_MESSAGE: 'Select a tax rate',
            TAX_RATE_ACCOUNT_TYPE_MISMATCH_VALIDATION_MESSAGE: 'Tax rate must match account type',
        },
        ACCOUNT_AND_TAX_SETUP_TAB: {
            TITLE: 'Account & tax setup',
            ACCOUNT_AND_TAX_SETUP_DETAILS: (
                <div>
                    <p>
                        Select an account and tax rate for each transaction type. Where a transaction type has an
                        account and tax rate pre-populated, review these and update if required.
                    </p>
                    <p>
                        If you select an account with a <b>New</b> tag, these will be created in your Xero organisation.
                    </p>
                </div>
            ),
            PRIMARY_TAX_REGION_TITLE: 'Your primary tax region',
            PRIMARY_TAX_REGION: {
                AU: 'Australia (AU)',
                NZ: 'New Zealand (NZ)',
                UK: 'United Kingdom (UK)',
            },
            PRIMARY_BLURB: (
                <span>
                    Your primary tax region is the country or region where you collect sales tax in Shopify and report
                    in Xero. All other regions are categorised as ‘Global’.{' '}
                    <Link displayLinkIcon={true} href={'https://central.xero.com/s/article/Connect-to-Shopify'}>
                        Learn about tax regions
                    </Link>{' '}
                </span>
            ),
            GLOBAL_BLURB: (
                <span>
                    All regions you sell to are categorised as ‘Global’.{' '}
                    <Link displayLinkIcon={true} href={'https://central.xero.com/s/article/Connect-to-Shopify'}>
                        Learn about tax regions
                    </Link>{' '}
                </span>
            ),
            CONFIRMATION_BLURB: (
                <span>
                    Confirm the account and tax rates are correct before proceeding. <br />
                    If you're unsure, speak with your advisor or{' '}
                    <Link
                        displayLinkIcon={true}
                        href={
                            'https://www.xero.com/advisors/find-advisors/?type=advisors&tag[]=xero:advisor-directory/industries-served/e-commerce&orderBy=ADVISOR_RELEVANCE&sort=ASC&pageNumber=1'
                        }
                    >
                        find one in Xero's advisory directory
                    </Link>
                </span>
            ),
        },
    },
    ERROR: {
        CHART_OF_ACCOUNT_NOT_FOUND: {
            MESSAGE: 'Chart of account has been deleted in Xero. Transactions from Shopify can’t be imported.',
            ACTION: 'Manage settings',
            ACTION_URL: '',
        },
        TAX_RATE_NOT_FOUND: {
            MESSAGE: 'Tax rate has been deleted in Xero. Transactions from Shopify can’t be imported.',
            ACTION: 'Manage settings',
            ACTION_URL: '',
        },
        TAX_RATE_INCORRECT: {
            MESSAGE:
                'Chart of account type or tax type has been changed in Xero. The chart of account and tax rate is no longer valid. Transactions from Shopify can’t be imported.',
            ACTION: 'Manage settings',
            ACTION_URL: '',
        },
        MISSING_TRANSACTION_TYPE_MAPPING: {
            MESSAGE:
                'A new transaction type has been detected. Please select an account and tax rate for this transaction type to continue importing Shopify orders.',
            ACTION: 'Manage settings',
            ACTION_URL: '',
        },
        BANK_ACCOUNT_NOT_FOUND: {
            MESSAGE:
                "The bank account for a payment gateway has been deleted. Transactions from Shopify can't be imported until a new bank account is added.",
            ACTION: 'Manage settings',
            ACTION_URL: '',
        },
        CLEARING_ACCOUNT_NOT_FOUND: {
            MESSAGE:
                'The clearing account for a payment gateway has been deleted. Check the payment gateway details to make sure transactions from Shopify can still be imported.',
            ACTION: 'Manage settings',
            ACTION_URL: '',
        },
        CONTACT_NOT_FOUND: {
            MESSAGE: "Shopify contact has been deleted. Transactions from Shopify can't be imported.",
            ACTION: 'Manage settings',
            ACTION_URL: '',
        },
        INVOICE_LIMIT_REACHED: {
            MESSAGE: 'The maximum number of invoices generated per month on this plan has been reached.',
            ACTION: 'Manage plan',
            ACTION_URL: '//xero.com/pricing-plans/',
        },
        CONNECTION_ERROR: {
            MESSAGE:
                'Oops, we’ve had a problem connecting Shopify to Xero. Please click the ‘Disconnect app’ button below, and try connecting the app again.',
            ACTION: '',
            ACTION_URL: '',
        },
        BILLING_SUBSCRIPTION_CANCELLED: {
            MESSAGE:
                'It looks like there is a problem with your subscription. To continue using Shopify, please check your subscription details.',
            ACTION: 'Update subscription',
            ACTION_URL: '',
        },
        FREE_TRIAL_EXPIRED: {
            MESSAGE:
                'Your 30-day free trial on the Shopify integration by Xero has expired. Subscribe now to continue importing transactions from Shopify.',
            ACTION: 'Subscribe now',
            ACTION_URL: '',
        },
        INSUFFICIENT_USER_PERMISSIONS: {
            MESSAGE:
                'The user assigned to the integration no longer has admin permissions. An admin user must resubmit the manage settings flow to update the assigned user. Transactions from Shopify cannot be imported until this process has been completed.',
            ACTION: 'Manage settings',
            ACTION_URL: '',
        },
    },
    EDUCATION_FLOW,
    EDUCATION_FLOW_TITLE: 'Connect Shopify sales to Xero',
    UPDATE: {
        HEADING: 'Setup Shopify',
        CONTENT_PART_1:
            'A new version of the Shopify integration by Xero is now available. Complete set up to experience the improved sales tax functionality.',
        CONTENT_PART_2: '',
        CONTENT_URL: '',
        CONTENT_DISPLAY_LINK: '',
    },
    LEGAL_TEXT: (
        <div>
            By setting up this integration, you acknowledge that your use of this integration is subject to Xero's{' '}
            <a href='https://www.xero.com/nz/about/legal/terms/?escape=true' target='_blank'>
                terms
            </a>{' '}
            and{' '}
            <a href='https://www.xero.com/nz/about/privacy/?escape=true' target='_blank'>
                privacy
            </a>
            .
        </div>
    ),
    ORGANISATION: {
        HEADING: 'Choose an organisation to connect with ',
        INTEGRATION_NAME: 'Shopify',
        CONNECT_ORG: 'Connect',
        CANCEL_CONNECT: 'Cancel',
        DROPDOWN_SELECT: 'Select organisation',
        VALIDATION_MESSAGE: 'The currency your Shopify store uses must match the currency set in Xero',
        ADMIN_PERMISSIONS_REQUIRED: 'Admin permission required',
        ONLY_ORG_ALREADY_CONNECTED_ERROR_MESSAGE:
            'No Xero organisation available. If you wish to connect this Shopify store, you will need to disconnect the integration from your Xero organisation.',
    },
    FREE_PLAN: {
        HEADING: 'Basic',
        FEATURES: [
            'Syncs daily Shopify transactions',
            'Daily summary invoice per payment gateway',
            'Invoice breakdown by sales/discounts/shipping/refunds/gift cards/fees',
            'Optional use of clearing accounts for Shopify Payments and Paypal',
            'Bank account reconciliation for Shopify Payments and Paypal',
            '90-day sales history import',
        ],
        CAPSULE: (
            <XUICapsule
                className='xui-margin-left-2xsmall xui-heading-small xui-textcolor-muted xui-text-deemphasis'
                invalidText={'[Placeholder] Invalid'}
            >
                Free
            </XUICapsule>
        ),
    },
    PAID_PLAN: {
        HEADING: 'Tax Plus',
        FEATURES: ['Includes all Basic benefits', 'Sales tax'],
        CAPSULE: (
            <XUICapsule
                className='xui-margin-left-2xsmall xui-heading-small xui-textcolor-muted xui-text-deemphasis xui-background-grey-6'
                invalidText={'[Placeholder] Invalid'}
            >
                Coming soon
            </XUICapsule>
        ),
    },
};

export default lang;

export type ShopifyLang = typeof lang;
